import { Component, Inject, OnInit } from '@angular/core';
import { ModalData, ModalRef, ModalService } from '@purespectrum1/ui/modal';
import { Router } from '@angular/router';
import { BuyerApiService } from '../../../shared/services/buyer-api/survey.service';
import { ConfirmationModalComponent } from '../../../shared/ui/confirmation-modal/confirmation-modal.component';
import {
  Quotas,
  TcResponse,
  Survey,
  CreateTcPayload,
} from '../../../shared/interfaces/survey.interface';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { EventEmitterService } from '../../../shared/services/event-emitter/event-emitter.service';
import { SurveyService } from '../../../shared/services/survey/survey.service';
import { Constants } from '../../constants/traffic-channel-modal.constants';
import { notifyMessage } from '../../../shared/constants/notify-message';
import { ViewRepondentModalComponent } from '../view-repondent-modal/view-repondent-modal.component';
import { ClearUploadModalComponent } from '../clear-upload-modal/clear-upload-modal.component';
import { SURVEY_STATUS } from '../../../shared/constants/survey-status';
import { AidaSelectedQuota, QbpSelectedQuota } from '../domain/selected-quota';
import { QuotaOption } from '../domain/selected-quota-option';

@Component({
  selector: 'ps-ui-traffic-channel-modal',
  templateUrl: './traffic-channel-modal.component.html',
  styleUrls: ['./traffic-channel-modal.component.css'],
})
export class TrafficChannelModalComponent implements OnInit {
  tcType = '';
  tcSuveyName = '';
  tcSurveyNamePrefix = '';
  channelType = '';
  hasPSIDRefList = false;
  psidFilePath = '';
  uploadedPsid: Array<{ psid: string }> = [];
  survey: Survey = {} as Survey;
  quotas: Array<Quotas> = [];
  quotaOption: Array<QuotaOption> = [];
  selectedQuota = '';
  copyTargetingFromParent: boolean = true;

  constructor(
    @Inject(ModalData)
    private _modalData: {
      type: string;
      survey: Survey;
      surveyQuotas: Quotas[];
      tcSurveys: TcResponse[];
    },
    private _toastr: ToasterService,
    private _modalRef: ModalRef<TrafficChannelModalComponent, string>,
    private _surveyService: SurveyService,
    private _buyerApiService: BuyerApiService,
    private _modal: ModalService,
    private _router: Router,
    private _eventEmitterService: EventEmitterService
  ) {}
  tcSurveys: TcResponse[] = [];

  ngOnInit(): void {
    this.tcType = this._modalData.type;
    this.tcSuveyName = this._modalData.survey.title;
    this.survey = this._modalData.survey;
    this.quotas = this._modalData.surveyQuotas;
    this.tcSurveys = this._modalData.tcSurveys;
    this._getSurveyNamePrefix();
    this._getQuotasForOptions();
  }

  public get qbpSelectedQuota(): QbpSelectedQuota {
    return new QbpSelectedQuota(this.survey, this.quotas);
  }

  public get aidaSelectedQuota(): AidaSelectedQuota {
    return new AidaSelectedQuota(this.survey, this.quotas);
  }

  private _getSurveyNamePrefix() {
    switch (this.tcType) {
      case 'REPLICA':
        this.tcSurveyNamePrefix = 'TC-R_';
        this.channelType = 'REP';
        break;
      case 'UNTARGETED':
        this.tcSurveyNamePrefix = 'TC-UT_';
        this.channelType = 'UNT';
        break;
      case 'SPECIFIC AUDIENCE':
        this.tcSurveyNamePrefix = 'TC-SA_';
        this.channelType = 'SPA';
        break;
      case 'TARGETED LIST':
        this.tcSurveyNamePrefix = 'TC-TL_';
        this.channelType = 'TL';
        break;
    }
  }

  checkTcSurveys() {
    const match = this.tcSurveys.find((tcSurvey) => {
      return (
        tcSurvey.channel_type == this.channelType &&
        tcSurvey.status != SURVEY_STATUS.CLOSED
      );
    });
    if (
      match &&
      !['SPECIFIC AUDIENCE', 'TARGETED LIST'].includes(this.tcType)
    ) {
      this.close();
      this._openConfirmationModal();
    } else {
      this.createTrafficChannelSurvey();
    }
  }
  createTrafficChannelSurvey() {
    const hasValidAidaSelectedQuota = this._hasValidAidaSelectedQuota();
    if (!hasValidAidaSelectedQuota) {
      return this._toastr.warning(
        notifyMessage.warningMessage.TRAFFIC_CHANNEL_MODAL
          .AIDA_QUOTA_OPTION_NOT_SELECTED
      );
    }

    const hasValidQbpSelectedQuota = this._hasValidQbpSelectedQuota();
    if (!hasValidQbpSelectedQuota) {
      return this._toastr.warning(
        notifyMessage.warningMessage.TRAFFIC_CHANNEL_MODAL
          .QBP_QUOTA_OPTION_NOT_SELECTED
      );
    }
    const payload = this._getCreateTcPaload();
    this._buyerApiService
      .createTrafficChannelSurvey(this._modalData.survey.id, payload)
      .subscribe(
        (response) => {
          this.close('Created');
          this._eventEmitterService.sendTcConfirmation('created');
          if (this.tcType === 'SPECIFIC AUDIENCE') {
            // TO-DO create survey page
            this._router.navigate([`/surveys/${response.surveyId}/edit`]);
          }
        },
        (error) => {
          this.close();
          if (error.error && error.error.msg) {
            return this._toastr.error(error.error.msg);
          }
          this._toastr.error(notifyMessage.errorMessage.API_ERROR);
        }
      );
  }

  private _hasValidAidaSelectedQuota() {
    return this.aidaSelectedQuota.enabled() ? !!this.selectedQuota : true;
  }

  private _hasValidQbpSelectedQuota() {
    return this.qbpSelectedQuota.enabled() ? !!this.selectedQuota : true;
  }

  private _getQuotaPayload(quotaType: 'qbp' | 'aida'): QuotaOption {
    const isEnabled =
      quotaType === 'qbp'
        ? this.qbpSelectedQuota.enabled()
        : this.aidaSelectedQuota.enabled();

    if (!isEnabled) {
      return <QuotaOption>{};
    }

    if (this.selectedQuota === 'all') {
      return { id: 'all' } as QuotaOption;
    }

    const selectedQuotaIndex = this.quotaOption.findIndex(
      (quota) => quota.category === this.selectedQuota
    );

    return selectedQuotaIndex > -1
      ? this.quotaOption[selectedQuotaIndex]
      : <QuotaOption>{};
  }

  private _getCreateTcPaload(): CreateTcPayload {
    return {
      channelSurveyName: this.tcSurveyNamePrefix + this.tcSuveyName,
      channelType: this.channelType,
      payload: {
        filePath: this.tcType === 'TARGETED LIST' ? this.psidFilePath : '',
      },
      qbpQuota: this._getQuotaPayload('qbp'),
      aidaQuota: this._getQuotaPayload('aida'),
      copyTargetingFromParent: this.copyTargetingFromParent,
    };
  }

  downloadFile(filename: string) {
    window.open(`/assets/templates/${filename}`, '_blank');
  }

  uploadPSID(event: Event) {
    const target = event.target as HTMLInputElement;
    const file: File = (target.files as FileList)[0];
    let fdata = new FormData();
    fdata.append('psid_ref_file', file);
    fdata.append('surveyId', this._modalData.survey.id.toString());
    this._surveyService.uploadPSIDFile(fdata).subscribe(
      (response) => {
        if (response.data.uniq_psids && response.data.uniq_psids.length) {
          this.hasPSIDRefList = true;
          this.psidFilePath = response.data.filePath;
          this.uploadedPsid = response.data.psids;
          this._toastr.success(
            `${response.data.uniq_psid} out of ${response.data.uploaded_psid} PSID’s were successfully uploaded`
          );
        } else {
          this.hasPSIDRefList = false;
          this.psidFilePath = '';
          this.uploadedPsid = [];
          this._toastr.error(notifyMessage.errorMessage.EMPTY_PSID_ERROR);
        }
      },
      (error: any) => {
        this.hasPSIDRefList = false;
        this._toastr.error(notifyMessage.errorMessage.API_ERROR);
      }
    );
  }

  close(msg = '') {
    this._modalRef.close(msg);
  }

  openViewModal() {
    this._modal.open(ViewRepondentModalComponent, {
      data: this.uploadedPsid,
      height: '80%',
      width: '80%',
    });
  }

  private _openConfirmationModal() {
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: Constants.CONFIRM_MODAL.TEXT,
        onlyOneTCLive: Constants.CONFIRM_MODAL.ONLY_ONE_TRAFFIC_CHANNEL_LIVE,
        confirmButtonLabel: Constants.CONFIRM_MODAL.CONFIRM_BUTTON_TEXT,
      },
    });
    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'ok') {
        this.createTrafficChannelSurvey();
      }
    });
  }

  openClearModal() {
    const modalRef = this._modal.open(ClearUploadModalComponent, {
      data: this.psidFilePath,
    });
    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'Clear') {
        this.hasPSIDRefList = false;
        this.psidFilePath = '';
        this.uploadedPsid = [];
      }
    });
  }

  private _getQuotasForOptions() {
    if (this.aidaSelectedQuota.enabled()) {
      this.quotaOption = this.aidaSelectedQuota.quotaOptions();
      return;
    }

    if (this.qbpSelectedQuota.enabled()) {
      this.quotaOption = this.qbpSelectedQuota.quotaOptions();
      return;
    }

    this.quotaOption = [];
  }
}
